import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  BrowserOutlined,
  CalendarFilled,
  CalendarOutlined,
  CategoryFilled,
  CategoryOutlined,
  ExperimentFilled,
  ExperimentOutlined,
  GearFilled,
  GearOutlined,
  MegaphoneOutlined,
  UserGroupFilled,
  UserGroupOutlined,
} from '@duxton-web/icons';
import { Flex, Menu, theme, Tooltip, Typography } from '@duxton-web/ui';
import { Link } from 'react-router-dom';

import { urlPattern } from '@/common/const/Regex';
import { appendEnvParam } from '@/utils/navigation';

interface MainMenuItemProps {
  to?: string;
  key: string;
  title: ReactNode;
  icon:
    | 'experiment'
    | 'calendar'
    | 'variables'
    | 'groups'
    | 'admin'
    | 'code-o'
    | 'browser'
    | 'feedback'
    | 'none';
  style?: React.CSSProperties;
  linkStyle?: React.CSSProperties;
  action?: () => void;
  selected?: boolean;
  disabled?: boolean;
  tooltip?: ReactNode;
}

const MainMenuItem = ({
  to = '',
  title,
  icon,
  action,
  style,
  linkStyle,
  selected = false,
  disabled = false,
  tooltip,
  ...rest
}: MainMenuItemProps) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (action) {
        e.preventDefault();
        action();
      }
    },
    [action],
  );

  const linkProps = useMemo(
    () => ({
      to: appendEnvParam(to),
      onClick: action ? handleClick : undefined,
    }),
    [to, action, handleClick, appendEnvParam],
  );

  const iconStyle = useMemo(
    () => ({
      fontSize: 20,
    }),
    [],
  );

  const Icons = useMemo(
    () => ({
      SELECTED: {
        calendar: <CalendarFilled style={iconStyle} />,
        variables: <CategoryFilled style={iconStyle} />,
        groups: <UserGroupFilled style={iconStyle} />,
        experiment: <ExperimentFilled style={iconStyle} />,
        admin: <GearFilled style={iconStyle} />,
        browser: <BrowserOutlined style={iconStyle} />,
        feedback: <MegaphoneOutlined style={iconStyle} />,
      },
      UNSELECTED: {
        calendar: <CalendarOutlined style={iconStyle} />,
        variables: <CategoryOutlined style={iconStyle} />,
        groups: <UserGroupOutlined style={iconStyle} />,
        experiment: <ExperimentOutlined style={iconStyle} />,
        admin: <GearOutlined style={iconStyle} />,
        browser: <BrowserOutlined style={iconStyle} />,
        feedback: <MegaphoneOutlined style={iconStyle} />,
      },
    }),
    [iconStyle],
  );

  const iconToUse = useMemo(
    () => Icons[selected ? 'SELECTED' : 'UNSELECTED']?.[icon] || undefined,
    [Icons, selected, icon],
  );

  const menuTextStyle = useMemo(
    () => ({
      color: selected ? theme.getDesignToken().colorPrimary : null,
      fontWeight: selected ? 600 : 400,
    }),
    [selected],
  );

  const MenuText = useCallback(
    () => (
      <Typography.Text disabled={disabled} style={menuTextStyle}>
        {title}
      </Typography.Text>
    ),
    [disabled, menuTextStyle, title],
  );

  const tooltipStyle = useMemo(
    () => ({
      backgroundColor: 'white',
      padding: '12px 16px',
      width: 400,
    }),
    [],
  );

  const TooltipWrapper = useCallback(
    ({ children }: { children: ReactNode }) => {
      if (!tooltip) return <>{children}</>;

      return (
        <Tooltip overlayInnerStyle={tooltipStyle} title={tooltip}>
          {children}
        </Tooltip>
      );
    },
    [tooltip, tooltipStyle],
  );

  const Content = useCallback(
    () => (
      <Flex align="center" gap={16}>
        {iconToUse}
        <MenuText />
      </Flex>
    ),
    [iconToUse],
  );

  const menuStyle = useMemo(
    () => ({
      ...style,
      paddingLeft: 12,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
    }),
    [style],
  );

  const handleMenuItemClick = useCallback(() => {
    if (urlPattern.test(to)) {
      window.open(to, '_blank');
    }
  }, [to]);

  const renderMenu = useCallback(() => {
    const content = <Content />;

    if (disabled || urlPattern.test(to)) {
      return <TooltipWrapper>{content}</TooltipWrapper>;
    }

    return (
      <TooltipWrapper>
        <Link {...linkProps} style={linkStyle}>
          {content}
        </Link>
      </TooltipWrapper>
    );
  }, [disabled, to, Content, TooltipWrapper, linkProps, linkStyle]);

  return (
    <Menu.Item
      {...rest}
      disabled={disabled}
      style={menuStyle}
      title=""
      onClick={handleMenuItemClick}
    >
      {renderMenu()}
    </Menu.Item>
  );
};

export default MainMenuItem;
